import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Warning, Flag, Camera, Clock, Check, X } from "@phosphor-icons/react";

const ICON = {
  overdue: { Icon: Clock, color: "text-[#E63946]" },
  incident: { Icon: Flag, color: "text-[#E63946]" },
  photos: { Icon: Camera, color: "text-[#F28C28]" },
};

const rel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const mins = Math.round((now - d) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export default function NotificationBell({ variant = "desktop" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const tz = new Date().getTimezoneOffset();
      const { data } = await api.get(`/notifications?tz_offset=${tz}`);
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (ids) => {
    if (!ids.length) return;
    setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - ids.filter((id) => items.find((n) => n.id === id && !n.read)).length));
    try { await api.post(`/notifications/read`, { ids }); } catch { /* silent */ }
  };

  const openJob = (n) => {
    setOpen(false);
    markRead([n.id]);
    nav(`/jobs/${n.job_id}`);
  };

  const markAll = async () => {
    const tz = new Date().getTimezoneOffset();
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await api.post(`/notifications/read?tz_offset=${tz}`, { all: true }); } catch { /* silent */ }
  };

  const isDrawer = variant === "drawer";
  const btnCls = variant === "desktop"
    ? "hidden md:flex fixed top-5 right-20 z-40 w-11 h-11 rounded-full bg-white border-2 border-[#3BB54A] text-[#3BB54A] items-center justify-center shadow-[0px_2px_8px_rgba(0,0,0,0.12)] hover:bg-[#EAF7EC] transition-colors"
    : isDrawer
    ? "w-full flex items-center gap-3 px-4 py-3 rounded-[10px] text-[15px] font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors"
    : "relative p-2";

  const panelInner = (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="font-display font-bold text-[#2E2E2E]">Notifications</span>
        <div className="flex items-center gap-1">
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notification-mark-all" className="text-xs font-bold text-[#3BB54A] flex items-center gap-1 px-2 py-1 rounded-md hover:bg-secondary">
              <Check size={13} weight="bold" /> Mark all read
            </button>
          )}
          <button onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-secondary text-muted-foreground"><X size={16} /></button>
        </div>
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
            <Bell size={28} className="mx-auto mb-2 opacity-40" />
            You're all caught up.
          </div>
        ) : (
          items.map((n) => {
            const { Icon, color } = ICON[n.type] || ICON.overdue;
            return (
              <button key={n.id} onClick={() => openJob(n)} data-testid={`notification-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${n.read ? "opacity-55" : ""}`}>
                <Icon size={18} weight="fill" className={`${color} mt-0.5 shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#2E2E2E] leading-snug">{n.message}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{rel(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#3BB54A] mt-1.5 shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <div ref={ref} className={variant === "desktop" ? "" : isDrawer ? "w-full" : "relative"}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`notification-bell-${variant}`} aria-label="Notifications" className={btnCls}>
        <span className={isDrawer ? "relative flex items-center justify-center" : ""}>
          <Bell size={22} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && !isDrawer && (
            <span data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </span>
        {isDrawer && (
          <>
            <span>Notifications</span>
            {unread > 0 && (
              <span data-testid="notification-badge"
                className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </>
        )}
      </button>

      {open && isDrawer && (
        <>
          <div className="fixed inset-0 z-[75] bg-black/40" data-testid="notification-backdrop" onClick={() => setOpen(false)} />
          <div data-testid="notification-panel"
            className="fixed z-[80] top-16 left-1/2 -translate-x-1/2 w-[340px] max-w-[92vw] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.18)] border border-border overflow-hidden">
            {panelInner}
          </div>
        </>
      )}

      {open && !isDrawer && (
        <div data-testid="notification-panel"
          className={`${variant === "desktop" ? "fixed top-[68px] right-6" : "absolute top-11 right-0"} z-[60] w-[340px] max-w-[92vw] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.18)] border border-border overflow-hidden`}>
          {panelInner}
        </div>
      )}
    </div>
  );
}
